<template>
  <el-row
      v-show="product"
      :gutter="20"
      class="el-collapse-item product-introduction-tab">
    <el-col :span="12">
      <BaseTableDetail
          :table-set-list="descriptionInfo1"
          :display-item="product"
          :editable="editable"
          :is-copied-all="isCopiedAll"
          :highlight-path-list="highlightPathList"
          validate-scope="descriptionInfo" />
    </el-col>
    <el-col :span="12">
      <BaseTableDetail
          :table-set-list="descriptionInfo2"
          :display-item="product"
          :editable="editable"
          :is-copied-all="isCopiedAll"
          :highlight-path-list="highlightPathList"
          validate-scope="descriptionInfo" />
    </el-col>
  </el-row>
</template>

<style scoped>

.product-introduction-tab ::v-deep .base-cell input,
.product-introduction-tab ::v-deep .base-cell i {
  height: 24px;
  line-height: 24px;
}
.product-introduction-tab ::v-deep .base-cell button i {
  height: initial;
  line-height: initial;
}

.product-introduction-tab ::v-deep .base-cell .el-tag--info {
  height: 20px;
  line-height: 20px;
}

.product-introduction-tab ::v-deep .base-cell .el-tag--info i {
  height: 16px;
  line-height: 16px;
}

.product-introduction-tab ::v-deep .base-cell .el-tag--info:first-child {
  margin-left: 5px;
}
</style>

<script>
import _ from 'lodash'
import { mapState, mapGetters, mapActions } from 'vuex'
import * as define from '@/define'
import utilProduct from '@/utils/product'

import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseTableDetail from '@/components/molecules/BaseTableDetail'

const EDITABLE = Object.freeze({
  [define.MODE.NEW]: Object.freeze({
    productDescription: true, productDetail: true,
    catchphrase: true, mediaCoverage: true, tags: true,
    seoMeasurement: true, appeal: true, remarks: true, attribute: true
  }),
  [define.MODE.EDIT]: Object.freeze({
    productDescription: true, productDetail: true,
    catchphrase: true, mediaCoverage: true, tags: true,
    seoMeasurement: true, appeal: true, remarks: true, attribute: true
  })
})
export default {
  name: 'ProductDescription',
  components: {
    BaseTableDetail
  },
  props: {
    product: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: Number,
      default: define.MODE.NORMAL
    },
    originData: {
      type: Object,
      default: () => ({})
    },
    storePath: {
      type: String,
      required: true
    },
    isCopiedAll: {
      type: Boolean,
      default: false
    },
    highlightPathList: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
    }
  },
  computed: {
    define: () => define,
    ...mapState({
      customizeMaster: (state) => state.Product.customizeMaster
    }),
    ...mapGetters({
      productUneditableItem: 'productUneditableItem',
      productEditable: 'productEditable'
    }),
    ...mapGetters('ProductDetail', {
      productDetailProperties: 'productDetailProperties'
    }),
    // カスタマイズ属性の親権限キー
    parentPermissionKey () { return define.PermissionKeys.Product.Detail.IntroductionInfo.ATTRIBUTE },
    editable () {
      let returnValue = _.cloneDeep(EDITABLE[this.mode] || {})
      const setValue = this.mode === define.MODE.EDIT
        ? (this.product.isEditable || 0) !== 0
        : true
      // 品番の編集フラグが0である場合、品番に紐づく項目は編集不可にする(サービスサイトで指定した値を除く)
      if ([define.MODE.NEW, define.MODE.EDIT].includes(this.mode)) {
        [].concat(this.descriptionInfo1, this.descriptionInfo2)
          .forEach((obj, data) => {
            if (_.get(obj, 'properties.0.editableId', null) != null) {
              _.set(returnValue, obj.properties[0].editableId, setValue)
            }
          })
        this.productEditable.forEach((key) => {
          _.set(returnValue, key, true)
        })
      }

      // 編集 かつ 既に注文がある場合、サービスサイトで設定されている項目は編集不可にする
      if (this.mode === define.MODE.EDIT && this.product.isOrdered) {
        this.productUneditableItem.forEach((key) => {
          _.set(returnValue, key, false)
        })
      }

      Object.keys(EDITABLE[this.mode] || {}).forEach((key) => {
        if (!EDITABLE[this.mode][key]) _.set(returnValue, key, false)
      })

      return returnValue
    },

    descriptionInfo1 () {
      const returnValue = [
        {
          label: '商品説明',
          properties: [ {
            type: 'String', path: 'productDescription', elementType: ELEMENT_TYPE.INPUT_TEXT_AREA,
            rows: 4, editableId: 'productDescription',
            class: 'multiLineTextBox', lineBreak: true,
            funcId: define.PermissionKeys.Product.Detail.IntroductionInfo.DESCRIPTION
          },
          {
            id: 'previewBtn',
            type: 'String', elementType: ELEMENT_TYPE.BUTTON, class: 'previewBtn',
            icon: '', size: 'small', label: 'プレビュー',
            path: 'productDescription',
            click: this.htmlClick,
            funcId: define.PermissionKeys.Product.Detail.IntroductionInfo.DESCRIPTION
          }]
        },
        {
          label: '商品詳細',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.INPUT_TEXT_AREA, path: 'productDetail',
            editableId: 'productDetail',
            rows: 4, class: 'multiLineTextBox', lineBreak: true,
            funcId: define.PermissionKeys.Product.Detail.IntroductionInfo.DETAIL
          },
          {
            id: 'previewBtn',
            type: 'String', elementType: ELEMENT_TYPE.BUTTON, class: 'previewBtn',
            icon: '', size: 'small', label: 'プレビュー',
            path: 'productDetail',
            click: this.htmlClick,
            funcId: define.PermissionKeys.Product.Detail.IntroductionInfo.DETAIL
          }]
        },
        {
          label: 'キャッチコピー',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.INPUT_TEXT_AREA, path: 'catchphrase',
            editableId: 'catchphrase',
            rows: 4, class: 'multiLineTextBox', lineBreak: true,
            funcId: define.PermissionKeys.Product.Detail.IntroductionInfo.CATCH_PHRASE
          }]
        },
        {
          label: '掲載情報',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.INPUT_TEXT_AREA, path: 'mediaCoverage',
            editableId: 'mediaCoverage',
            rows: 4, class: 'multiLineTextBox', lineBreak: true,
            funcId: define.PermissionKeys.Product.Detail.IntroductionInfo.MEDIA_COVERAGE
          }]
        },
        {
          label: 'タグ',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX, path: 'tags',
            editableId: 'tags', codeName: 'm_product.tags', selectCode: 'm_product.tags',
            funcId: define.PermissionKeys.Product.Detail.IntroductionInfo.TAG
          }]
        }
      ]

      let index = 1
      // カスタマイズ属性を設定
      if (this.customizeMaster != null &&
        this.$isFunctionShown(this.parentPermissionKey)) {
        this.customizeMaster.filter((element) => {
          // 商品種類共通のカスタマイズ属性である
          // 商品説明タブである
          // 表示位置が左の段であること
          return (element.functionKey === define.CUSTOMIZE_PROPERTY_KEY.PRODUCT.INTRODUCTION.COMMON.LEFT)
        }).sort((a, b) => {
          return a.displayOrder - b.displayOrder
        }).forEach((customizeObj) => {
          let emptyCellNum = customizeObj.displayOrder - index
          while (emptyCellNum > 1) {
            returnValue.push(utilProduct.getCustomizeEmptyColumn())
            emptyCellNum -= 1
          }
          returnValue.push(utilProduct.getCustomizeColumn(this.product, customizeObj, 'attributes', 'attribute',
            this.storePath, 'ProductDetail/productDetail', this.parentPermissionKey))

          index = customizeObj.displayOrder
        })
      }

      return returnValue
    },
    descriptionInfo2 () {
      const returnValue = [
        {
          label: 'SEO対策情報',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.INPUT_TEXT_AREA, path: 'seoMeasurement',
            editableId: 'seoMeasurement',
            rows: 4, class: 'multiLineTextBox', lineBreak: true,
            funcId: define.PermissionKeys.Product.Detail.IntroductionInfo.SEO_MEASUREMENT
          }]
        },
        {
          label: '訴求ポイント',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.RADIO_BUTTON, path: 'appeal', class: 'radio-button',
            editableId: 'appeal', codeName: 'm_product.product_category', selectCode: 'm_product.apeeal',
            funcId: define.PermissionKeys.Product.Detail.IntroductionInfo.APPEAL
          }]
        },
        {
          label: '備考',
          properties: [ {
            type: 'String', elementType: ELEMENT_TYPE.INPUT_TEXT_AREA, path: 'remarks',
            editableId: 'remarks',
            rows: 4, class: 'multiLineTextBox', lineBreak: true,
            funcId: define.PermissionKeys.Product.Detail.IntroductionInfo.REMARKS
          }]
        }
      ]

      let index = 1
      // カスタマイズ属性を設定
      if (this.customizeMaster != null &&
        this.$isFunctionShown(this.parentPermissionKey)) {
        this.customizeMaster.filter((element) => {
          // 商品種類共通のカスタマイズ属性である
          // 商品説明タブである
          // 表示位置が右の段であること
          return (element.functionKey === define.CUSTOMIZE_PROPERTY_KEY.PRODUCT.INTRODUCTION.COMMON.RIGHT)
        }).sort((a, b) => {
          return a.displayOrder - b.displayOrder
        }).forEach((customizeObj) => {
          let emptyCellNum = customizeObj.displayOrder - index
          while (emptyCellNum > 1) {
            returnValue.push(utilProduct.getCustomizeEmptyColumn())
            emptyCellNum -= 1
          }
          returnValue.push(utilProduct.getCustomizeColumn(this.product, customizeObj, 'attributes', 'attribute',
            this.storePath, 'ProductDetail/productDetail', this.parentPermissionKey))

          index = customizeObj.displayOrder
        })
      }

      return returnValue
    }
  },
  beforeUpdate () {
    const properties = [].concat(this.descriptionInfo1, this.descriptionInfo2)
    const propertyArray = []
    const attributePropertyArray = []
    properties.forEach((properties) => {
      const property = properties.properties[0] || {}
      const path = property.path || ''
      if ((typeof path === 'string' && path !== '') &&
        (property.funcId == null || !this.$isFunctionDisabled(property.funcId))) {
        const pathArray = path.split('.')
        // ドット区切りで分割して、通常項目か属性情報か判定
        // ex.
        // 通常項目：name→['name']
        // 属性情報：attributes.8.attrValue→['attributes','8','attrValue']
        if (pathArray.length > 1 && pathArray[0] === 'attributes') {
          // pathがドット区切りかつ分割した最初の要素が「attributes」→attributes
          attributePropertyArray.push(property)
        } else {
          propertyArray.push(property)
        }
      }
    })
    this.fetchProductDetailProperties({ propertyArray: propertyArray, type: 'introduction' })
    this.fetchProductDetailProperties({ propertyArray: attributePropertyArray, type: 'introductionAttribute' })
  },
  methods: {
    ...mapActions('ProductDetail', [
      'fetchProductDetailProperties'
    ]),
    htmlClick (event, displayItem, displayValue) {
      this.$store.dispatch({
        type: 'set',
        states: {
          [`${this.storePath}/htmlPreviewDialogVisible`]: true,
          [`${this.storePath}/htmlPreviewDialogContent`]: displayValue
        }
      })
    }
  }
}
</script>
